import { useEffect, useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import DescribeItPanel from "./DescribeItPanel";
import type { WatchlistDraft } from "./DescribeItPanel";
import BuilderStart from "./BuilderStart";
import YourWatchlists from "./YourWatchlists";
import NewsquawkLibraryList from "./NewsquawkLibraryList";

import { conciergeEnabled } from "@/onboarding/concierge/api";
import { useWatchlistPush } from "@/demo-guide/useWatchlistPush";
import { useSearchParams } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { usePortfoliosCRUD } from "../api/usePortfoliosCRUD";
import type { EditableWatchlist } from "../listTypes";
import type { FilterDraft } from "../hooks/useFilterBuilder";

interface BuilderCanvasProps {
  /** The concierge's hand-off of a combination to the builder. */
  onOpenInBuilder: (draft: FilterDraft) => void;
  /** The concierge's hand-off of a single proposed watchlist. */
  onOpenWatchlist: (draft: WatchlistDraft) => void;
  /** "Build it myself": the builder, empty. */
  onBuildManually: () => void;
  onEditWatchlist: (item: EditableWatchlist) => void;
  /** A library list, opened in the builder as an editable copy. */
  onBuildOn: (item: EditableWatchlist) => void;
}

/**
 * The Watchlists page's landing.
 *
 * On arrival: the two ways to start a list, and underneath them your lists
 * beside the Newsquawk library, newest of yours first under "Recently
 * created". "Build it for me" takes the whole width; "Build it myself", and
 * any list you open, go to the builder over it, and saving there brings you
 * back here with what you just made at the top.
 *
 * Your lists used to sit in a rail on the right, as reference for the form.
 * But the form is gone once you save, so the landing was two buttons beside a
 * narrow column you had to scan to find out whether the save had worked.
 */
export default function BuilderCanvas({
  onOpenInBuilder,
  onOpenWatchlist,
  onBuildManually,
  onEditWatchlist,
  onBuildOn,
}: BuilderCanvasProps) {
  const { authenticated } = useAuth();
  const { portfoliosQuery } = usePortfoliosCRUD();
  const portfolios = portfoliosQuery.data || [];

  /** Logged-out visitor sent here by the guided demo's last step. */
  const watchlistPush = useWatchlistPush();

  /**
   * ?describe=1 opens the assisted route, as if the card had been pressed.
   *
   * No longer used by the guided demo, which now asks the visitor to press the
   * card themselves — a rail button that opened this panel did nothing at all
   * on a second press, and doing it FOR them meant the run could complete
   * without the product being touched. See BUILD_ON_BUILDER.
   *
   * Kept as a deep link: it is the only way to land straight on the assisted
   * route, which is worth having for testing and for linking people to it.
   * Same shape as the ?create= handling in PortfolioBuilderBrowser: act once,
   * then clear the param so a refresh or a back-navigation does not
   * re-trigger it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("describe") !== "1") return;
    setDescribeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("describe");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [describeOpen, setDescribeOpen] = useState(false);

  /**
   * When the landing was last left for the concierge or the builder.
   *
   * Anything created after it is what this visit produced, and is the "just
   * made" highlight when the list comes back. A time rather than an id
   * because the concierge can make several lists in one go, and all of them
   * are the answer to what the person just did.
   */
  const [leftLandingAt, setLeftLandingAt] = useState<number | null>(null);
  useEffect(() => {
    if (describeOpen) setLeftLandingAt(Date.now());
  }, [describeOpen]);
  /** The builder opens over the landing, but going there is leaving it. */
  const toBuilder =
    <A extends unknown[]>(go: (...args: A) => void) =>
    (...args: A) => {
      setLeftLandingAt(Date.now());
      go(...args);
    };

  /**
   * The concierge's hand-offs to the builder end its turn here.
   *
   * What it proposed now lives in the builder — DescribeItPanel clears its own
   * plan and box on the way out — so leaving the panel open behind it meant
   * saving dropped you back onto an empty "Build it for me", with the list
   * you had just made nowhere in sight. Closing it puts the landing
   * underneath the builder, so the save lands where every other one does:
   * your lists, newest first, with the flare on what you just made.
   */
  const handOffToFilterBuilder = (draft: FilterDraft) => {
    setDescribeOpen(false);
    onOpenInBuilder(draft);
  };
  const handOffWatchlist = (draft: WatchlistDraft) => {
    setDescribeOpen(false);
    onOpenWatchlist(draft);
  };

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      // Read by the container in PortfolioBuilderBrowser to size itself.
      data-building={describeOpen ? "" : undefined}
    >
      {describeOpen ? (
        /* The assisted route gets the whole container, rather than being
           squeezed into the half-width card it was launched from. What it
           produces then lands in the builder, pre-filled — see
           `onOpenWatchlist`. */
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <div className="w-full px-5 py-5">
            <div className="flex items-center gap-2">
              <SparklesIcon className="size-4 shrink-0 text-gray-400" />
              <h2 className="flex-1 text-xs font-semibold uppercase tracking-[0.1em] text-gray-300">
                Build it for me
              </h2>
              <button
                type="button"
                onClick={() => setDescribeOpen(false)}
                className="cursor-pointer text-xs text-gray-500 transition-colors hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
            <div className="mt-4">
              <DescribeItPanel
                variant="hero"
                onOpenInBuilder={handOffToFilterBuilder}
                onOpenWatchlist={handOffWatchlist}
                onCreated={() => setDescribeOpen(false)}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Sized to what it holds: the lists cap their own height, as the
           widget menus do. On a short screen, where the container is capped
           below that, the lists give way first — they shrink and scroll inside
           themselves, keeping their bottom padding — and the landing itself
           scrolls only once they are down to a few rows. Scrolling the whole
           landing instead cut the lists off flush with the container's edge. */
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <BuilderStart
            onManual={toBuilder(onBuildManually)}
            onDescribe={conciergeEnabled ? () => setDescribeOpen(true) : undefined}
            demoPush={watchlistPush}
          />
          {/* A logged-out visitor can't own a list, so there is nothing to
              show — and an empty panel under the demo's two cards would only
              compete with the one it is asking them to press. */}
          {authenticated && (
            <div className="flex min-h-32 flex-col px-5 pb-5">
              {/* Two columns on the same grid as the cards above, so the
                  width beside your lists holds the other half of the job
                  instead of standing empty. */}
              <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
                <YourWatchlists
                  portfolios={portfolios}
                  onEdit={toBuilder(onEditWatchlist)}
                  highlightSince={leftLandingAt}
                />
                <NewsquawkLibraryList
                  portfolios={portfolios}
                  onBuildOn={toBuilder(onBuildOn)}
                  onEdit={toBuilder(onEditWatchlist)}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
